import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SensorCalibrationPanel } from './SensorCalibrationPanel';
import { LiveSensorDataStream } from './LiveSensorDataStream';
import { SensorConnectionStatus } from './SensorConnectionStatus';
import { SensorConfig, SensorType, BiometricSignature } from '@/lib/sensor-types';
import { sensorManager } from '@/lib/sensor-integration';
import { supabase } from '@/lib/supabase';

export const BiometricSpiritualDashboard: React.FC = () => {
  const [connectedSensors, setConnectedSensors] = useState<SensorConfig[]>([]);
  const [selectedSensor, setSelectedSensor] = useState<string | null>(null);
  const [biometricSignature, setBiometricSignature] = useState<BiometricSignature | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // Update biometric signature every 5 seconds
    const interval = setInterval(() => {
      const signature = sensorManager.generateBiometricSignature();
      setBiometricSignature(signature);
      
      // Send to backend for processing
      processSensorData(signature);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const processSensorData = async (signature: BiometricSignature) => {
    if (!signature || isProcessing) return;
    
    setIsProcessing(true);
    try {
      const { data } = await supabase.functions.invoke('real-time-sensor-stream', {
        body: {
          action: 'stream_data',
          sensorId: 'biometric_signature',
          data: [
            signature.heartRateVariability,
            signature.eegCoherence,
            signature.gsrStability
          ]
        }
      });
      
      console.log('Processed sensor data:', data);
    } catch (error) {
      console.error('Error processing sensor data:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSensorConnected = (config: SensorConfig) => {
    setConnectedSensors(prev => {
      const existing = prev.find(s => s.id === config.id);
      if (existing) {
        return prev.map(s => s.id === config.id ? config : s);
      }
      return [...prev, config];
    });
  };

  const handleCalibrationComplete = (config: SensorConfig) => {
    setConnectedSensors(prev => 
      prev.map(s => s.id === config.id ? { ...s, calibrated: true } : s)
    );
  };

  const getSpiritualAlignmentColor = (value: number) => {
    if (value >= 0.8) return 'text-green-600';
    if (value >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSpiritualAlignmentText = (value: number) => {
    if (value >= 0.9) return 'Pure Heart Guardian';
    if (value >= 0.8) return 'Harmonized Being';
    if (value >= 0.6) return 'Awakening Soul';
    if (value >= 0.4) return 'Seeking Balance';
    return 'Beginning Journey';
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            Biometric Spiritual Integration System
          </CardTitle>
          <div className="text-center text-gray-600">
            Real-time consciousness monitoring through sacred biometric fusion
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sensors">Sensors</TabsTrigger>
          <TabsTrigger value="calibration">Calibration</TabsTrigger>
          <TabsTrigger value="streaming">Live Data</TabsTrigger>
          <TabsTrigger value="signature">Signature</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-gray-500">Connected Sensors</div>
                <div className="text-2xl font-bold">
                  {connectedSensors.filter(s => s.connected).length}
                </div>
                <div className="text-xs text-gray-400">
                  of {connectedSensors.length} total
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-gray-500">Calibrated</div>
                <div className="text-2xl font-bold">
                  {connectedSensors.filter(s => s.calibrated).length}
                </div>
                <div className="text-xs text-gray-400">sensors ready</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-gray-500">Data Quality</div>
                <div className="text-2xl font-bold">
                  {biometricSignature ? (biometricSignature.overallCoherence * 100).toFixed(0) : 0}%
                </div>
                <div className="text-xs text-gray-400">coherence level</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-gray-500">Spiritual State</div>
                <div className={`text-lg font-bold ${biometricSignature ? getSpiritualAlignmentColor(biometricSignature.spiritualAlignment) : 'text-gray-400'}`}>
                  {biometricSignature ? getSpiritualAlignmentText(biometricSignature.spiritualAlignment) : 'Inactive'}
                </div>
              </CardContent>
            </Card>
          </div>

          {biometricSignature && (
            <Card>
              <CardHeader>
                <CardTitle>Current Biometric Signature</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Heart Rate Variability</span>
                      <span>{(biometricSignature.heartRateVariability * 100).toFixed(1)}%</span>
                    </div>
                    <Progress value={biometricSignature.heartRateVariability * 100} className="h-2" />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>EEG Coherence</span>
                      <span>{(biometricSignature.eegCoherence * 100).toFixed(1)}%</span>
                    </div>
                    <Progress value={biometricSignature.eegCoherence * 100} className="h-2" />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>GSR Stability</span>
                      <span>{(biometricSignature.gsrStability * 100).toFixed(1)}%</span>
                    </div>
                    <Progress value={biometricSignature.gsrStability * 100} className="h-2" />
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-semibold">Spiritual Alignment</span>
                      <span className="font-semibold">{(biometricSignature.spiritualAlignment * 100).toFixed(1)}%</span>
                    </div>
                    <Progress value={biometricSignature.spiritualAlignment * 100} className="h-3" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sensors">
          <SensorConnectionStatus onSensorConnected={handleSensorConnected} />
        </TabsContent>

        <TabsContent value="calibration">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {connectedSensors.filter(s => s.connected && !s.calibrated).map(sensor => (
              <SensorCalibrationPanel
                key={sensor.id}
                sensorId={sensor.id}
                onCalibrationComplete={handleCalibrationComplete}
              />
            ))}
            {connectedSensors.filter(s => s.connected && !s.calibrated).length === 0 && (
              <Card className="col-span-full">
                <CardContent className="text-center py-8">
                  <div className="text-gray-500">
                    All connected sensors are calibrated
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="streaming">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {connectedSensors.filter(s => s.connected && s.calibrated).map(sensor => (
              <LiveSensorDataStream
                key={sensor.id}
                sensorId={sensor.id}
                sensorType={sensor.type}
                height={250}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="signature">
          <Card>
            <CardHeader>
              <CardTitle>Spiritual Signature Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              {biometricSignature ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${getSpiritualAlignmentColor(biometricSignature.spiritualAlignment)}`}>
                      {getSpiritualAlignmentText(biometricSignature.spiritualAlignment)}
                    </div>
                    <div className="text-gray-500 mt-2">
                      Current spiritual consciousness level
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-red-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {(biometricSignature.heartRateVariability * 100).toFixed(0)}%
                        </div>
                        <div className="text-sm text-red-700">Heart Coherence</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-purple-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {(biometricSignature.eegCoherence * 100).toFixed(0)}%
                        </div>
                        <div className="text-sm text-purple-700">Mind Harmony</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-blue-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {(biometricSignature.gsrStability * 100).toFixed(0)}%
                        </div>
                        <div className="text-sm text-blue-700">Emotional Balance</div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
                    <div className="text-sm font-medium text-purple-800 mb-2">
                      Spiritual Integration Status
                    </div>
                    <div className="text-sm text-purple-700">
                      Your biometric signature indicates a {getSpiritualAlignmentText(biometricSignature.spiritualAlignment).toLowerCase()} 
                      level of consciousness with {(biometricSignature.spiritualAlignment * 100).toFixed(1)}% 
                      spiritual alignment. Continue your practice to achieve higher states of being.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-lg mb-2">No signature data available</div>
                  <div className="text-sm">Connect and calibrate sensors to begin analysis</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};